import { useEffect, useMemo, useRef } from 'react'
import Konva from 'konva'
import type { KonvaEventObject } from 'konva/lib/Node'
import { Group, Rect, Text } from 'react-konva'
import {
  LINK_TEXT_COLOR,
  TEXT_FONT_FAMILY,
  TEXT_LINE_HEIGHT,
} from '../constants'
import { TEXT_CONTENT_NAME, layoutTextRuns } from './textLayout'
import {
  LinkActivation,
  TextLink,
  isLinkModifierPressed,
  openTextLink,
} from './textLinks'

type Props = {
  x: number
  y: number
  width: number
  text: string
  fontSize: number
  fill: string
  visible: boolean
  links: TextLink[]
  activation?: LinkActivation
}

type HoverState = { fallbackCursor: string; showsPointer: boolean }

const LinkedText = ({
  x,
  y,
  width,
  text,
  fontSize,
  fill,
  visible,
  links,
  activation,
}: Props) => {
  const groupRef = useRef<Konva.Group>(null)
  const hoverRef = useRef<HoverState | null>(null)
  const keyListenerRef = useRef<((event: KeyboardEvent) => void) | null>(null)
  const lineHeightPx = fontSize * TEXT_LINE_HEIGHT
  const runs = useMemo(
    () => layoutTextRuns(text, links, width, fontSize),
    [text, links, width, fontSize],
  )

  const isActive = (event: { metaKey: boolean; ctrlKey: boolean }) =>
    activation === 'click' || isLinkModifierPressed(event)

  const updateCursor = (modifierPressed: boolean) => {
    const hover = hoverRef.current
    const container = groupRef.current?.getStage()?.container()
    if (!hover || !container) return
    const wantsPointer = activation === 'click' || modifierPressed
    if (wantsPointer === hover.showsPointer) return
    if (wantsPointer) hover.fallbackCursor = container.style.cursor
    container.style.cursor = wantsPointer ? 'pointer' : hover.fallbackCursor
    hover.showsPointer = wantsPointer
  }

  const removeKeyListener = () => {
    const listener = keyListenerRef.current
    if (!listener) return
    window.removeEventListener('keydown', listener)
    window.removeEventListener('keyup', listener)
    keyListenerRef.current = null
  }

  useEffect(() => removeKeyListener, [])

  const handleMouseEnter = () => {
    hoverRef.current = { fallbackCursor: '', showsPointer: false }
    if (activation !== 'modifier-click') return
    removeKeyListener()
    const listener = (event: KeyboardEvent) =>
      updateCursor(isLinkModifierPressed(event))
    keyListenerRef.current = listener
    window.addEventListener('keydown', listener)
    window.addEventListener('keyup', listener)
  }

  const handleMouseLeave = () => {
    const hover = hoverRef.current
    const container = groupRef.current?.getStage()?.container()
    if (hover?.showsPointer && container) {
      container.style.cursor = hover.fallbackCursor
    }
    hoverRef.current = null
    removeKeyListener()
  }

  const stopIfActive = (event: KonvaEventObject<MouseEvent | TouchEvent>) => {
    if (isActive(event.evt)) event.cancelBubble = true
  }

  const handleClick =
    (link: TextLink) => (event: KonvaEventObject<MouseEvent>) => {
      if (!isActive(event.evt)) return
      event.cancelBubble = true
      openTextLink(link)
    }

  return (
    <Group
      ref={groupRef}
      name={TEXT_CONTENT_NAME}
      x={x}
      y={y}
      width={width}
      text={text}
      fontSize={fontSize}
      visible={visible}
      listening={activation !== undefined}
    >
      {runs.map((run, index) => (
        <Text
          key={`text-${index}`}
          x={run.x}
          y={run.y}
          text={run.text}
          fontSize={fontSize}
          fontFamily={TEXT_FONT_FAMILY}
          lineHeight={TEXT_LINE_HEIGHT}
          fill={run.link ? LINK_TEXT_COLOR : fill}
          wrap='none'
          listening={false}
        />
      ))}
      {runs.map(({ link, ...run }, index) =>
        link ? (
          <Rect
            key={`link-${index}`}
            x={run.x}
            y={run.y}
            width={run.width}
            height={lineHeightPx}
            fill='transparent'
            // Touches on read-only links must still scroll the page; a tap then opens the link through the browser's emulated click.
            preventDefault={activation !== 'click'}
            onMouseEnter={handleMouseEnter}
            onMouseMove={(event) =>
              updateCursor(isLinkModifierPressed(event.evt))
            }
            onMouseLeave={handleMouseLeave}
            onMouseDown={stopIfActive}
            onDblClick={stopIfActive}
            onClick={handleClick(link)}
            onTap={activation === 'click' ? stopIfActive : undefined}
          />
        ) : null,
      )}
    </Group>
  )
}

export default LinkedText
